/**
 * Color constants for consistent styling across the application
 */

// ─── Fixed Industry → Color mapping ──────────────────────────────────────────
// Each industry always gets the SAME color regardless of filter state, panel,
// or sort order.  Colors are muted siblings of the charts.set1 brand palette
// (teal / orange / rusty / olive / gold family) — same hue DNA, slightly
// shifted so they don't exactly duplicate the chart colors.
//   • WCAG contrast ≥ 3.0 on both light (#fff) and dark (#1a1a1a) backgrounds
//   • Inter-industry distance ≥ 50 so they're clearly distinguishable
//
// Light variants (autumn / default themes, white-ish backgrounds)
const INDUSTRY_COLORS_LIGHT = {
  'Life Sciences & Health':            '#a3443c',  // Muted Rusty Red      (sibling of #af4b3e)
  'Energy & Environmental Systems':    '#1a6070',  // Deep Teal            (sibling of #1c566b)
  'Semiconductors & Electronics':      '#7b5ea7',  // Muted Purple          (distinct from teal)
  'Chemical & Process Engineering':    '#c87028',  // Warm Orange           (sibling of #d67c29)
  'Food & Agriculture':                '#a07040',  // Warm Sienna           (earthy brown, distinct from sage)
  'Metallurgy & Engineering':          '#cc9840',  // Amber Gold            (sibling of #e8b357)
  'Forestry & Bio-Based Materials':    '#7a8a5a',  // Sage Olive            (sibling of #c7bf7f)
};

// Dark-theme variants (brightened for contrast on dark backgrounds)
const INDUSTRY_COLORS_DARK = {
  'Life Sciences & Health':            '#d06858',  // Bright Rusty          (sibling of #d46b5c)
  'Energy & Environmental Systems':    '#58b0c0',  // Bright Teal           (sibling of #5cb8cc)
  'Semiconductors & Electronics':      '#a080c8',  // Bright Purple         (distinct from teal)
  'Chemical & Process Engineering':    '#e09848',  // Warm Amber            (sibling of #e8a04a)
  'Food & Agriculture':                '#c89868',  // Bright Sienna         (earthy brown, distinct from sage)
  'Metallurgy & Engineering':          '#e8b868',  // Bright Gold           (sibling of #f0be78)
  'Forestry & Bio-Based Materials':    '#a0b078',  // Soft Sage             (sibling of #d4cc82)
};

// Fallback palette for industries not in the hardcoded map.
// Colors are drawn from the same muted brand-palette family as the fixed entries.
// A deterministic hash of the industry name picks one → same name always gets same color.
const INDUSTRY_DYNAMIC_LIGHT = [
  '#a3443c',  // Muted Rusty Red
  '#1a6070',  // Deep Teal
  '#7b5ea7',  // Muted Purple
  '#c87028',  // Warm Orange
  '#a07040',  // Warm Sienna
  '#cc9840',  // Amber Gold
  '#7a8a5a',  // Sage Olive
  '#3a6e60',  // Forest Green
  '#7a4a6a',  // Mauve
  '#4a6a8a',  // Steel Blue
  '#8a6a3a',  // Warm Brown
  '#5a7a4a',  // Moss Green
];
const INDUSTRY_DYNAMIC_DARK = [
  '#d06858',  // Bright Rusty
  '#58b0c0',  // Bright Teal
  '#a080c8',  // Bright Purple
  '#e09848',  // Warm Amber
  '#c89868',  // Bright Sienna
  '#e8b868',  // Bright Gold
  '#a0b078',  // Soft Sage
  '#58a898',  // Bright Forest Green
  '#b878a8',  // Bright Mauve
  '#789ab8',  // Bright Steel Blue
  '#c8984a',  // Warm Copper
  '#88b868',  // Bright Moss
];

/** Simple djb2-style hash → stable palette index for any string. */
const hashIndustryName = (name, paletteLen) => {
  let h = 5381;
  for (let i = 0; i < name.length; i++) h = ((h << 5) + h) ^ name.charCodeAt(i);
  return Math.abs(h) % paletteLen;
};

/**
 * Get the fixed colour for an industry name.
 * Priority: client API overrides → hardcoded map → deterministic hash palette.
 * Known industries use the hardcoded map; unknown ones get a stable colour
 * derived from a hash of their name so they're never just gray.
 * @param {string} industryName - exact string from predicted_industry column
 * @param {'autumn'|'dark'|'default'} [themeName='autumn'] - current theme
 * @returns {string} hex colour
 */

// Client-supplied overrides set once from /api/config (industry_colors).
// Stored at module level so no prop-drilling is required.
let _clientIndustryOverrides = {};

/**
 * Set per-client industry color overrides from /api/config response.
 * Call once after fetching config. Overrides take priority over the
 * built-in hardcoded map.
 * @param {Object} colorMap - { "Industry Name": "#hexcolor", ... }
 */
export const setClientIndustryColors = (colorMap) => {
  _clientIndustryOverrides = (colorMap && typeof colorMap === 'object') ? colorMap : {};
};

export const getIndustryColor = (industryName, themeName = 'autumn') => {
  if (_clientIndustryOverrides[industryName]) return _clientIndustryOverrides[industryName];
  const map = themeName === 'dark' ? INDUSTRY_COLORS_DARK : INDUSTRY_COLORS_LIGHT;
  if (map[industryName]) return map[industryName];
  const palette = themeName === 'dark' ? INDUSTRY_DYNAMIC_DARK : INDUSTRY_DYNAMIC_LIGHT;
  return palette[hashIndustryName(industryName, palette.length)];
};

/**
 * Get the full industry → colour map for the given theme.
 * Merges (in priority order): client API overrides → hardcoded map → hash fallback for extras.
 * Pass extraIndustries to ensure any names not in static/override maps still appear in legends.
 */
export const getIndustryColorMap = (themeName = 'autumn', extraIndustries = []) => {
  const base = themeName === 'dark' ? { ...INDUSTRY_COLORS_DARK } : { ...INDUSTRY_COLORS_LIGHT };
  // Client overrides win over the built-in map
  Object.assign(base, _clientIndustryOverrides);
  // Any extra names not yet covered get a hash-based colour
  extraIndustries.forEach(name => {
    if (!base[name]) base[name] = getIndustryColor(name, themeName);
  });
  return base;
};

// Default Theme Colors (Previously COLORS)
export const DEFAULT_COLORS = {
  // Primary UI colors - Updated based on image palette
  primary: {
    teal: '#028fa3',        // Main teal color for headers (from image)
    tealLight: '#71ccd1',   // Light teal for tags/backgrounds (from image)
    tealMuted: '#51a7b0',   // Muted teal derived from #028fa3/#71ccd1

    orange: '#e8b357',      // Main orange for buttons/accents (from image)
    orangeDark: '#d09a3f',  // Darker orange derived from #e8b357
    orangeLight: '#f5e0b8', // Lighter orange derived from #e8b357

    purple: '#b14a79',      // Muted purple for accents/buttons (from image)
    purpleDark: '#933a63',  // Darker purple derived from #b14a79

    olive: '#aea24e',       // Olive green accent (from image)
  },

  // Text colors - Kept for readability
  text: {
    dark: '#424242',        // Main text color - light mode
    light: '#ECEFF1',       // Main text color - dark mode
    muted: '#757575',       // Muted text - light mode
    mutedLight: '#B0BEC5',  // Muted text - dark mode
  },

  // Chart colors - Updated with image palette + existing for variety
  charts: {
    set1: [
      '#028fa3',  // New Teal
      '#e8b357',  // New Orange
      '#b14a79',  // New Purple
      '#aea24e',  // New Olive
      '#71ccd1',  // New Light Teal
      '#78909C',  // Blue-grey
      '#FF8A65',  // Coral
      '#4DB6AC',  // Old Light Teal
    ]
  },

  // Node types for network graph - Updated with image palette
  nodes: {
    organization: '#028fa3', // New Teal (generic fallback)
    orgCompany: '#2e86ab',   // Steel Blue - Companies
    orgUniversity: '#a23b72', // Berry - Universities
    orgOther: '#8e7c68',     // Warm Taupe - Other orgs
    orgUnspecified: '#c4b7a6', // Light Sand - Unspecified orgs
    beamline: '#e8b357',     // New Orange
    affiliation: '#b14a79',  // New Purple
    default: '#aea24e'       // New Olive (replaces grey)
  }
};

// Autumn Theme Colors - Updated based on image palette for a calming, fresh look
export const AUTUMN_COLORS = {
  primary: {
    teal: '#1c566b',        // Teal/Blue-Green from image
    tealLight: '#5c8a9a',   // Lighter version derived from #1c566b
    tealMuted: '#3a788f',   // Muted version derived from #1c566b

    orange: '#d67c29',      // Burnt Orange from image
    orangeDark: '#b86823',  // Darker version derived from #d67c29
    orangeLight: '#e5a96f', // Lighter version derived from #d67c29

    // Replacing previous purple/brown with Rusty Red from image
    purple: '#af4b3e',      // Rusty Red/Brown from image
    purpleDark: '#913c30',  // Darker version derived from #af4b3e

    olive: '#aea24e',       // Olive Green from image
  },
  text: { // Kept suitable for white background
    dark: '#333333',        // Dark Grey (more neutral than brown)
    light: '#F5F5F5',       // Off-white
    muted: '#757575',       // Medium Grey
    mutedLight: '#BDBDBD',  // Light Grey
  },
  charts: {
    set1: [
      '#1c566b', // Teal
      '#d67c29', // Orange
      '#af4b3e', // Rusty Red
      '#aea24e', // Olive
      '#e8b357', // Gold/Yellow Ochre
      '#5c8a9a', // Light Teal
      '#e5a96f', // Light Orange
      '#c7bf7f', // Light Olive (derived)
    ]
  },
  nodes: {
    organization: '#1c566b', // Teal (generic fallback)
    orgCompany: '#1a6e5c',   // Deep Teal-Green - Companies
    orgUniversity: '#8b4a5e', // Muted Mauve - Universities
    orgOther: '#7a6e5a',     // Warm Brown - Other orgs
    orgUnspecified: '#b8a994', // Muted Sand - Unspecified orgs
    beamline: '#d67c29',     // Orange
    affiliation: '#af4b3e',  // Rusty Red
    default: '#aea24e'       // Olive
  }
};

// Dark Theme Colors - Warm dark companion to the autumn palette
export const DARK_COLORS = {
  primary: {
    teal: '#5cb8cc',        // Brightened brand teal
    tealLight: '#7dcad8',   // Light teal for dark bg
    tealMuted: '#4a99ab',   // Muted teal

    orange: '#e8a04a',      // Warm orange for dark bg
    orangeDark: '#d68c35',  // Deeper orange
    orangeLight: '#f0be78', // Light orange

    purple: '#d46b5c',      // Brightened rusty red
    purpleDark: '#bf5647',  // Deeper rusty red

    olive: '#c4b85e',       // Brightened olive
  },
  text: {
    dark: '#E8E2DA',        // Warm light text
    light: '#FDFBF9',       // Warm white
    muted: '#9A918A',       // Warm muted
    mutedLight: '#B5ADA5',  // Lighter warm muted
  },
  charts: {
    set1: [
      '#5cb8cc', // Teal
      '#e8a04a', // Orange
      '#d46b5c', // Rusty Red
      '#c4b85e', // Olive
      '#7dcad8', // Light Teal
      '#f0be78', // Light Orange
      '#e0897a', // Light Rusty
      '#d4cc82', // Light Olive
    ]
  },
  nodes: {
    organization: '#5cb8cc', // Teal (generic fallback)
    orgCompany: '#4a9a7e',   // Muted Teal-Green
    orgUniversity: '#b07088', // Muted Mauve
    orgOther: '#8a8070',     // Warm Grey
    orgUnspecified: '#6a6058', // Warm Dark Grey
    beamline: '#e8a04a',     // Warm Orange
    affiliation: '#d46b5c',  // Rusty Red
    default: '#c4b85e'       // Olive
  }
};

// Brand colors from Tiller Alpha landing page
export const BRAND = {
  orange: '#E8922C',         // Primary landing page orange
  orangeDeep: '#D67C29',     // Deeper warm orange
  orangeLight: '#F0A848',    // Lighter accent orange
  orangePale: '#F5C882',     // Pale background orange
  amber: '#E8B357',          // Gold/amber accent
  warmWhite: '#FFF8F0',      // Warm off-white
  warmGrey: '#5C4A3A',       // Warm dark text
};

// ─── Category-Specific Palettes ─────────────────────────────────────────────
// Industry → fixed per-name (above).  Everything else → positional.
//
// Beamline, Topic & Affiliation all use the same diverse multi-hue palette.
// They never appear in the same chart, so no conflict.  Built from the beloved
// charts.set1 "original 8" and extended with 8 complementary hues = 16 total.
// Every color is a different hue for maximum distinguishability.
// (Beamline's orange identity is conveyed by its node shape in the network
// graph — bar charts should prioritise readability over hue branding.)

// ── Diverse Palette (Beamlines, Topics & Affiliations) ──────────────────────
const DIVERSE_PALETTE_LIGHT = [
  // ── Original 8 (charts.set1 autumn) ──
  '#1c566b',  // Teal
  '#d67c29',  // Orange
  '#af4b3e',  // Rusty Red
  '#aea24e',  // Olive
  '#e8b357',  // Gold
  '#5c8a9a',  // Light Teal
  '#e5a96f',  // Light Orange
  '#c7bf7f',  // Light Olive
  // ── 8 additional distinct hues ──
  '#7a5a8a',  // Muted Purple
  '#5a8a68',  // Sage Green
  '#c46878',  // Dusty Rose
  '#6a80a8',  // Steel Blue
  '#9a8058',  // Khaki
  '#4a7a78',  // Dark Teal
  '#b87858',  // Terracotta
  '#8a9a4a',  // Yellow-Green
];

const DIVERSE_PALETTE_DARK = [
  // ── Original 8 (charts.set1 dark) ──
  '#5cb8cc',  // Teal
  '#e8a04a',  // Orange
  '#d46b5c',  // Rusty Red
  '#c4b85e',  // Olive
  '#f0be78',  // Gold
  '#7dcad8',  // Light Teal
  '#e0897a',  // Light Rusty
  '#d4cc82',  // Light Olive
  // ── 8 additional distinct hues ──
  '#a888b8',  // Soft Purple
  '#78b090',  // Light Sage
  '#e090a0',  // Soft Rose
  '#88a0c8',  // Light Steel
  '#c0a878',  // Light Khaki
  '#68a8a0',  // Bright Teal
  '#d8a080',  // Light Terracotta
  '#b0c068',  // Bright Yellow-Green
];

/**
 * Get the colour palette for a data category.
 * @param {'beamline'|'affiliation'|'topic'} category
 * @param {'autumn'|'dark'|'default'} [themeName='autumn']
 * @returns {string[]} array of hex colours
 */
export const getCategoryPalette = (category, themeName = 'autumn') => {
  const isDark = themeName === 'dark';
  // All non-industry categories share the diverse multi-hue palette.
  // Industry uses its own fixed-per-name mapping (getIndustryColor).
  return isDark ? [...DIVERSE_PALETTE_DARK] : [...DIVERSE_PALETTE_LIGHT];
};

// Legacy export — diverse palette for backwards compatibility
export const DATA_PALETTE = [...DIVERSE_PALETTE_LIGHT];

// Helper function to get colors based on theme name - Autumn is default
export const getThemeColors = (themeName = 'autumn') => {
  if (themeName === 'dark') return DARK_COLORS;
  if (themeName === 'default') return DEFAULT_COLORS;
  return AUTUMN_COLORS;
};

// Default exports now use AUTUMN_COLORS
export const chartColors = AUTUMN_COLORS.charts.set1;
export const nodeColors = AUTUMN_COLORS.nodes;
